import React, { useMemo } from 'react';
import '../../styles/panels.css';

// VehicleDetailsPanel: professional casefile-style layout
// Props: vehicle { media, licensePlates[], marks[], attributes, color, make, model }, onClose
// Purpose: Detailed vehicle report with images, plates, marks, attributes, and overlay preview.
// Interplay: Opened by results views when user presses "Details" on a mini-card.

const VehicleDetailsPanel = ({ vehicle, onClose }) => {
  const plates = Array.isArray(vehicle?.licensePlates) ? vehicle.licensePlates : [];
  const marks = Array.isArray(vehicle?.marks) ? vehicle.marks : [];

  const bboxPreview = useMemo(() => {
    const img = vehicle?.media?.bestImageUrl;
    if (!img) return null;
    let box = vehicle?.attributes?.preview_box;
    if (!box) {
      // Derive from first bounding box when available
      const b = (vehicle?.attributes?.bounding_boxes || [])[0];
      if (b) {
        box = { x: b.x, y: b.y, width: b.width, height: b.height };
      }
    }
    if (!box) return null;
    const style = {
      position: 'relative', display: 'inline-block', maxWidth: '100%'
    };
    const overlay = {
      position: 'absolute', border: '2px solid #22c55e',
      left: box.x || 0, top: box.y || 0, width: box.width || 0, height: box.height || 0
    };
    return { img, style, overlay };
  }, [vehicle]);

  if (!vehicle) return null;
  return (
    <div className="vehicle-details-overlay" onClick={(e)=>{ if (e.target===e.currentTarget) onClose?.(); }}>
      <div className="vehicle-details-modal" onClick={(e)=>e.stopPropagation()}>
        <div className="vd-header">
          <div className="vd-title">Vehicle Report</div>
          <button className="vd-close" onClick={onClose}>×</button>
        </div>
        <div className="vd-body">
          <div className="vd-left">
            <div className="vd-photo">
              {vehicle.media?.bestImageUrl ? (
                <img src={vehicle.media.bestImageUrl} alt="vehicle" />
              ) : (
                <div className="vd-photo-placeholder">No Photo</div>
              )}
            </div>
            {vehicle.media?.bestPlateImageUrl && (
              <div className="vd-photo secondary">
                <img src={vehicle.media.bestPlateImageUrl} alt="plate" />
                <div className="vd-photo-caption">Plate</div>
              </div>
            )}
          </div>
          <div className="vd-right">
            {bboxPreview && (
              <div className="vd-section">
                <div className="vd-label">Overlay Preview</div>
                <div style={bboxPreview.style}>
                  <img src={bboxPreview.img} alt="overlay" />
                  <div style={bboxPreview.overlay} />
                </div>
              </div>
            )}
            <div className="vd-section">
              <div className="vd-label">Identification</div>
              <div className="vd-grid">
                <div className="vd-row"><span className="k">Make</span><span className="v">{vehicle.make || 'Unknown'}</span></div>
                <div className="vd-row"><span className="k">Model</span><span className="v">{vehicle.model || 'Unknown'}</span></div>
                <div className="vd-row"><span className="k">Color</span><span className="v">{vehicle.color || 'Unknown'}</span></div>
                <div className="vd-row"><span className="k">Confidence</span><span className="v">{Math.round((vehicle.confidence||0)*100)}%</span></div>
              </div>
            </div>
            <div className="vd-section">
              <div className="vd-label">License Plates</div>
              {plates.length === 0 ? <div className="vd-empty">None detected</div> : (
                <div className="vd-plates">
                  {plates.map(p => (
                    <div key={p.id || p.plateNumber} className="vd-plate">
                      <div className="plate-number">{p.plateNumber}</div>
                      <div className="plate-meta">{p.state || 'Unknown'} • {Math.round((p.confidence||0)*100)}%</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="vd-section">
              <div className="vd-label">Marks</div>
              {marks.length === 0 ? <div className="vd-empty">No marks observed</div> : (
                <div className="vd-marks">
                  {marks.map(m => (
                    <span key={m.id} className="vd-mark-chip">{m.label}</span>
                  ))}
                </div>
              )}
            </div>
            {Array.isArray(vehicle.attributes?.timeline_seconds) && vehicle.attributes.timeline_seconds.length > 0 && (
              <div className="vd-section">
                <div className="vd-label">Timeline</div>
                <div className="vd-marks">
                  {vehicle.attributes.timeline_seconds.slice(0,20).map((sec, idx) => (
                    <span key={idx} className="vd-mark-chip">{sec.toFixed(2)}s</span>
                  ))}
                </div>
              </div>
            )}
            <div className="vd-section">
              <div className="vd-label">Attributes</div>
              <div className="vd-grid">
                {Object.entries(vehicle.attributes || {}).slice(0, 12).map(([k,v]) => (
                  <div key={k} className="vd-row"><span className="k">{k}</span><span className="v">{String(v)}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailsPanel;


